import { useTheme } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DetailPageHeader from 'components/header/DetailPageHeader';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetLynPropertyQuery } from 'store/features/process/processApi';
import IdentityActionButton from 'static-solutions/IdentityActionButton';
import useIdentitySolutionAction from 'static-solutions/hook/useIdentitySolutionAction';
import { Button, Col, Collapse, Form, Input, Row, Radio, Select, InputNumber, Tooltip, DatePicker, Empty } from 'antd';
import dayjs from 'dayjs';

import historyIcon from 'assets/images/icons/history.svg';
import { UsergroupAddOutlined } from '@ant-design/icons';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import AddIconButton from 'components/button/AddIconButton';
import DeleteIconButton from 'components/button/DeleteIconButton';

import useGetIdentityPermissionHook from 'static-solutions/hook/useGetIdentityPermissionHook';
import SearchMemberModal from './SearchMemberModal';
import CustomTextInput from 'components/inputs/CustomTextInput';
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { DraggableRow } from './COECommittee';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { useGetCommitteeHistoryMutation } from 'store/features/comptroller/comptrollerApi';
import HistoryCom from './History';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import { formatDateToUTC } from 'utils/utils';
import SectionCard from 'components/section/SectionCard';

const Edit = ({ screen = 'view' }) => {
	const theme = useTheme();
	const { state } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const [historyState, setHistoryState] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [initialValues, setInitialValues] = useState({});
	const [selectedModalRow, setSelectedModalRow] = useState({
		name: null,
		index: null,
	});

	const { permissions, singleIdentityData, isSingleUninitialized, isSingleIdentityLoading, isSingleIdentitySuccess } = useGetIdentityPermissionHook(
		{
			id: state?._id,
			processCode: state?.processCode,
		}
	);

	const {
		action,
		modalData,
		confirmModal,
		handleUpdate,
		handleCreate,
		handleSubmit,
		formSubmitData,
		setConfirmModal,
		isOperationLoading,
		handleApproveReject,
		handleActiveDeactivate,
	} = useIdentitySolutionAction({
		permissions,
		id: state?._id,
		processCode: state?.processCode,
		version: +singleIdentityData?.version,
	});

	const { data: roleListRes } = useGetLynPropertyQuery(
		{
			type: 'FORM',
			fieldCode: '10042',
			propertyCode: 'PROPERTY_role',
			processVersion: '24A',
			status: 'Active',
		},
		{
			refetchOnMountOrArgChange: true,
			keepUnusedDataFor: 0,
		}
	);
	const [getUserIdentity, { data: userIdentityList }] = useSendControlDataMutation();
	const [GetCommitteeHistory, { data: historyData, isSuccess: isGetHistorySuccess }] = useGetCommitteeHistoryMutation();

	const callApiHistory = () => {
		if (!historyState) {
			GetCommitteeHistory({
				committeeId: initialValues?.refId,
			});
		}

		setHistoryState((prev) => !prev);
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event, fields, fieldsOpt) => {
		const { active, over } = event;

		if (active?.id && over?.id && active?.id !== over?.id) {
			const oldIndex = fields.findIndex((field) => `${field.key}` === active.id);
			const newIndex = fields.findIndex((field) => `${field.key}` === over.id);

			// Update the state or form fields with reordered data
			fieldsOpt.move(oldIndex, newIndex);
		}
	};

	const navigateToEdit = () => {
		navigate('/static/committee/edit', {
			state: { processCode: state?.processCode, processName: state?.processName, _id: state?._id, tab: state?.tab },
			replace: true,
		});
	};

	const dataList = useMemo(() => {
		const userOptions = userIdentityList?.responseData?.users?.map((u) => ({
			label: u.userFullName,
			value: u.userId,
			userFullName: u.userFullName,
		}));

		const positionOptions = userIdentityList?.responseData?.positions?.map((p) => ({
			label: p.positionName,
			value: p.propertyValue,
			userFullName: p.userFullName,
		}));

		const processRoleOptions = userIdentityList?.responseData?.processRoles?.map((r) => ({
			label: r.processRoleName,
			value: r.propertyValue,
			userFullName: r.userFullName,
		}));

		return {
			users: userOptions,
			positions: positionOptions,
			processRoles: processRoleOptions,
		};
	}, [userIdentityList?.status]);

	const roleList = useMemo(() => {
		return roleListRes?.lynProperties?.map((item) => ({
			label: item?.displayName,
			value: item?.propertyValue,
		}));
	}, [roleListRes?.lynProperties]);

	useEffect(() => {
		if (state?.processCode) {
			const formattedPayload = {
				data: '',
				processCode: state?.processCode,
				scriptName: 'employee-data-v2',
			};

			getUserIdentity(formattedPayload);
		} else {
			navigate('/static/committee');
		}
	}, [state?.processCode]);

	useEffect(() => {
		if (!isSingleIdentityLoading) {
			if (
				singleIdentityData?.parkedData &&
				Object.keys(singleIdentityData?.parkedData)?.length > 0 &&
				(state?.tab === 'W-I-P' || state?.tab === 'Worklist')
			) {
				form.setFieldsValue(singleIdentityData?.parkedData);
				setInitialValues(singleIdentityData?.parkedData);
			} else {
				form.setFieldsValue(singleIdentityData?.data);
				setInitialValues(singleIdentityData?.data);
			}
		}
	}, [isSingleIdentityLoading, singleIdentityData, state?.tab]);

	return (
		<>
			<Box>
				<DetailPageHeader
					image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
					title={screen.toLocaleLowerCase() === 'edit' ? `Edit ${state?.processName}` : `View ${state?.processName}`}
					showAuto
					autoFirst
					pinStorageKey={`detail-page-header:auto:committee:${screen?.toLowerCase?.() === 'edit' ? 'edit' : 'view'}`}
				/>

				<SectionCard>
					{/* Loading */}
					{isSingleUninitialized ||
						(isSingleIdentityLoading && (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									minHeight: '200px',
								}}
							>
								<CircularProgress color="primary" />
								<Typography variant="body2" sx={{ mt: 1 }}>
									Loading data...
								</Typography>
							</Box>
						))}

					{isSingleIdentitySuccess && (
						<Form.Provider
							onFormFinish={(name, { values, forms }) => {
								const { mainForm } = forms;

								if (name === 'committeeMembers') {
									const prev = mainForm.getFieldValue('committeeMembers') || [];
									const updated = [...prev];
									updated[selectedModalRow.index] = {
										...prev[selectedModalRow.index], // keep old
										...values, // merge new
									};
									mainForm.setFieldValue('committeeMembers', updated);
								} else if (name === 'committeeSecretary') {
									const prev = mainForm.getFieldValue('committeeSecretary') || [];
									const updated = [...prev];
									updated[selectedModalRow.index] = {
										...prev[selectedModalRow.index],
										...values,
									};
									mainForm.setFieldValue('committeeSecretary', updated);
								}
							}}
						>
							<Form
								form={form}
								layout="vertical"
								id="coe-committee"
								autoComplete="off"
								requiredMark={false}
								name="mainForm"
								onFinish={(data) =>
									handleSubmit({
										data: { ...initialValues, ...data, fromDate: formatDateToUTC(data?.fromDate) },
										actionType: 'update',
									})
								}
							>
								<Row gutter={[16, 0]}>
									<Col xs={24} sm={24} md={12} xl={12}>
										<CustomTextInput
											form={form}
											label="Committee Name"
											name="title"
											placeholder="Write here"
											disabled={screen === 'view' ? true : false}
											rules={[
												{
													required: true,
													message: 'Committee name is required.',
												},
											]}
										/>
									</Col>

									<Col xs={24} sm={24} md={12} xl={12}>
										<Form.Item
											label="Effective From"
											name="fromDate"
											getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
											rules={[
												{
													required: true,
													message: 'Field is required',
												},
											]}
										>
											<DatePicker style={{ width: '100%' }} disabled={screen === 'view' ? true : false} />
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item
											size="medium"
											name="purpose"
											label="Scope of Work / Terms of Reference"
											rules={[
												{
													required: true,
													message: 'Details is required.',
												},
											]}
										>
											<Input.TextArea rows={2} placeholder="Write here" disabled={screen === 'view' ? true : false} />
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Typography
											variant="h4"
											sx={{
												fontWeight: 600,
												fontSize: '18px',
												color: '#1a3b5d',
												marginBottom: '16px',
												padding: '0 0 12px 0',
												borderBottom: '1px solid #e0e6ed',
											}}
										>
											Committee Composition
										</Typography>
									</Col>
									{/* <MainCard content={false} sx={{ overflowX: 'auto', width: '100%', mb: 2 }}> */}
									<Form.List name="committeeMembers">
										{(fields, fieldsOpt) => (
											<DndContext
												sensors={sensors}
												collisionDetection={closestCenter}
												onDragEnd={(e) => handleDragEnd(e, fields, fieldsOpt)}
												modifiers={[restrictToVerticalAxis, restrictToParentElement]}
											>
												<TableContainer
													// sx={{
													// 	width: '100%',
													// 	overflowX: 'auto',
													// 	display: 'block',
													// 	'& td, & th': { whiteSpace: 'nowrap' },
													// 	'& td': {
													// 		borderTop: '0px',
													// 		borderLeft: '0px',
													// 		borderBottom: '0px',
													// 	},
													// 	'& td:not(:last-child)': {
													// 		borderRight: '1px solid #e0e0e0',
													// 	},
													// 	'& tr:not(:last-child)': {
													// 		borderBottom: '1px solid #e0e0e0',
													// 	},
													// 	'& th': {
													// 		borderBottom: '1px solid #e0e0e0',
													// 	},
													// }}
													sx={{
														'& td, & th': {
															border: '1px solid rgb(224, 224, 224)',
															p: 1,
														},
														'& .ant-form-item': {
															marginBottom: 0,
														},
														mb: 2,
														px: 1,
													}}
												>
													<Table
														aria-labelledby="committeeCompositionTable"
														// sx={{
														// 	// tableLayout: 'fixed',
														// 	width: '100%',
														// 	'& .MuiTableBody-root .MuiTableCell-root': {
														// 		p: 0.5,
														// 	},
														// 	'& .MuiTableHead-root .MuiTableCell-root': {
														// 		py: 1,
														// 		px: 2,
														// 	},
														// 	'& .ant-form-item': {
														// 		marginBottom: 0,
														// 	},
														// }}
													>
														<TableHead sx={{ backgroundColor: 'rgb(246, 245, 245)' }}>
															<TableRow>
																{screen === 'view' && (
																	<TableCell
																		align="center"
																		sx={{ minWidth: '50px', width: '3%', fontWeight: 'bold' }}
																	>
																		Serial
																	</TableCell>
																)}
																{screen !== 'view' && <TableCell sx={{ minWidth: '60px', width: '5%' }}></TableCell>}

																<TableCell sx={{ minWidth: '180px', width: '18%', fontWeight: 'bold' }}>
																	Committee
																</TableCell>
																<TableCell sx={{ minWidth: '120px', width: '12%', fontWeight: 'bold' }}>
																	Nominated As
																</TableCell>
																<TableCell sx={{ minWidth: '180px', width: '12%', fontWeight: 'bold' }}>
																	Role
																</TableCell>
																<TableCell sx={{ minWidth: '100px', width: '8%', fontWeight: 'bold' }}>
																	Approval Sequence
																</TableCell>
																<TableCell sx={{ minWidth: '100px', width: '10%', fontWeight: 'bold' }}>
																	Voting Right
																</TableCell>
																<TableCell sx={{ minWidth: '100px', width: '10%', fontWeight: 'bold' }}>
																	Is Voting Must?
																</TableCell>
																<TableCell sx={{ minWidth: '100px', width: '10%', fontWeight: 'bold' }}>
																	Voting Score
																</TableCell>
																{screen !== 'view' && (
																	<TableCell align="center" sx={{ minWidth: '100px', width: '10%' }}>
																		Action
																	</TableCell>
																)}
															</TableRow>
														</TableHead>
														<TableBody>
															{fields?.map((field, index) => (
																<SortableContext key={field.key} items={fields.map((field) => `${field.key}`)}>
																	<Form.Item name={[field.name, 'positionType']} noStyle />
																	<Form.Item name={[field.name, 'positionValue']} noStyle />
																	<DraggableRow id={`${field.key}`} screen={screen} index={index}>
																		<TableCell>
																			<Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
																				<Form.Item
																					name={[field.name, 'personName']}
																					style={{ margin: 0, flex: 1 }}
																				>
																					<Input placeholder="Person name" disabled />
																				</Form.Item>
																				{screen !== 'view' && (
																					<Button
																						type="dashed"
																						shape="circle"
																						icon={<UsergroupAddOutlined />}
																						onClick={() => {
																							setShowModal(true);
																							setSelectedModalRow({
																								name: 'committeeMembers',
																								index: field.name,
																							});
																						}}
																					/>
																				)}
																			</Box>
																		</TableCell>

																		<TableCell>
																			<Form.Item
																				name={[field.name, 'nominatedAs']}
																				rules={[{ required: true, message: 'Required' }]}
																			>
																				<Select
																					allowClear
																					showSearch
																					style={{ width: '100%' }}
																					placeholder="Select"
																					options={[
																						{ label: 'Position', value: 'positions' },
																						{ label: 'Employee', value: 'users' },
																						{ label: 'Process Role', value: 'processRoles' },
																					]}
																					disabled={true}
																				/>
																			</Form.Item>
																		</TableCell>

																		<TableCell>
																			<Form.Item
																				name={[field.name, 'positionName']}
																				rules={[{ required: true, message: 'Required' }]}
																			>
																				<Select
																					allowClear
																					showSearch
																					style={{ width: '100%' }}
																					placeholder="Select"
																					options={roleList}
																					disabled={screen === 'view'}
																				/>
																			</Form.Item>
																		</TableCell>

																		<TableCell>
																			<Form.Item
																				name={[field.name, 'serial']}
																				rules={[{ required: true, message: 'Required' }]}
																			>
																				<InputNumber
																					min={1}
																					style={{ width: '100%' }}
																					placeholder="Write here"
																					disabled={screen === 'view'}
																				/>
																			</Form.Item>
																		</TableCell>

																		<TableCell>
																			<Form.Item name={[field.name, 'canVote']}>
																				<Select
																					allowClear
																					showSearch
																					style={{ width: '100%' }}
																					placeholder="Select"
																					options={[
																						{ label: 'Yes', value: 'yes' },
																						{ label: 'No', value: 'no' },
																					]}
																					disabled={screen === 'view'}
																				/>
																			</Form.Item>
																		</TableCell>

																		<TableCell>
																			<Form.Item name={[field.name, 'isMandatory']}>
																				<Select
																					allowClear
																					showSearch
																					style={{ width: '100%' }}
																					placeholder="Select"
																					options={[
																						{ label: 'Yes', value: 'yes' },
																						{ label: 'No', value: 'no' },
																					]}
																					disabled={screen === 'view'}
																				/>
																			</Form.Item>
																		</TableCell>

																		<TableCell>
																			<Form.Item name={[field.name, 'points']} style={{ margin: 0, flex: 1 }}>
																				<Input
																					placeholder="Write here"
																					disabled={screen === 'view'}
																				/>
																			</Form.Item>
																		</TableCell>

																		{screen !== 'view' && (
																			<TableCell align="center">
																				{fields.length === 1 && index === 0 && (
																					<Tooltip title="Add Row">
																						<AddIconButton onClick={() => fieldsOpt.add()} />
																					</Tooltip>
																				)}

																				{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																					<Tooltip title="Delete Row">
																						<DeleteIconButton
																							onClick={() => fieldsOpt.remove(field.name)}
																						/>
																					</Tooltip>
																				)}

																				{fields.length > 1 && index === fields.length - 1 && (
																					<>
																						<Tooltip title="Add Row">
																							<AddIconButton onClick={() => fieldsOpt.add()} />
																						</Tooltip>
																						<Tooltip title="Delete Row">
																							<DeleteIconButton
																								onClick={() => fieldsOpt.remove(field.name)}
																							/>
																						</Tooltip>
																					</>
																				)}

																				{fields.length > 1 && index === 0 && <span></span>}
																			</TableCell>
																		)}
																	</DraggableRow>
																</SortableContext>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</DndContext>
										)}
									</Form.List>
									{/* </MainCard> */}

									<Col xs={24} sm={24} md={12} xl={12}>
										<Form.Item
											label="Quorum"
											name="quorum"
											rules={[
												{
													required: true,
													message: 'Quorum is required',
												},
											]}
										>
											<InputNumber
												min={0}
												style={{ width: '100%' }}
												placeholder="Write here"
												disabled={screen === 'view' ? true : false}
											/>
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Typography
											variant="h4"
											sx={{
												fontWeight: 600,
												fontSize: '18px',
												color: '#1a3b5d',
												marginBottom: '16px',
												padding: '0 0 12px 0',
												borderBottom: '1px solid #e0e6ed',
											}}
										>
											Committee Secretary
										</Typography>
									</Col>
									{/* <MainCard content={false} sx={{ overflowX: 'auto', width: '100%', mb: 2 }}> */}
									<Form.List name="committeeSecretary">
										{(fields, fieldsOpt) => (
											<TableContainer
												// sx={{
												// 	width: '100%',
												// 	overflowX: 'auto',
												// 	display: 'block',
												// 	'& td, & th': { whiteSpace: 'nowrap' },
												// 	'& td': {
												// 		borderTop: '0px',
												// 		borderLeft: '0px',
												// 		borderBottom: '0px',
												// 	},
												// 	'& td:not(:last-child)': {
												// 		borderRight: '1px solid #e0e0e0',
												// 	},
												// 	'& tr:not(:last-child)': {
												// 		borderBottom: '1px solid #e0e0e0',
												// 	},
												// 	'& th': {
												// 		borderBottom: '1px solid #e0e0e0',
												// 	},
												// }}
												sx={{
													'& td, & th': { border: '1px solid rgb(224, 224, 224)', p: 1 },
													'& .ant-form-item': {
														marginBottom: 0,
													},
													mb: 2,
													px: 1,
												}}
											>
												<Table
													aria-labelledby="committeeSecretaryTable"
													// sx={{
													// 	'& .MuiTableBody-root .MuiTableCell-root': {
													// 		p: 0.5,
													// 	},
													// 	'& .MuiTableHead-root .MuiTableCell-root': {
													// 		py: 1,
													// 		px: 2,
													// 	},
													// 	'& .ant-form-item': {
													// 		marginBottom: 0,
													// 	},
													// }}
												>
													<TableHead sx={{ backgroundColor: 'rgb(246, 245, 245)' }}>
														<TableRow>
															<TableCell sx={{ minWidth: '200px', width: '45%' }}>Name</TableCell>
															<TableCell sx={{ minWidth: '150px', width: '45%' }}>Nominated As</TableCell>
															{screen !== 'view' && (
																<TableCell sx={{ minWidth: '100px', width: '10%' }} align="center">
																	Action
																</TableCell>
															)}
														</TableRow>
													</TableHead>
													<TableBody>
														{fields?.map((field, index) => (
															<SortableContext key={field.key} items={fields.map((field) => `${field.key}`)}>
																<Form.Item name={[field.name, 'positionType']} noStyle />
																<Form.Item name={[field.name, 'positionValue']} noStyle />
																{/* <DraggableRow id={`${field.key}`}> */}
																<TableRow>
																	{/* <TableCell align="center" sx={{ width: '10rem' }}>
																				<Form.Item
																					name={[field.name, 'serial']}
																					rules={[
																						{
																							required: true,
																							message: 'Committee role is required.',
																						},
																					]}
																				>
																					<InputNumber
																						min={1}
																						style={{ width: '100%' }}
																						placeholder="Write here"
																					/>
																				</Form.Item>
																			</TableCell> */}

																	<TableCell>
																		<Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
																			<Form.Item
																				name={[field.name, 'personName']}
																				style={{ margin: 0, flex: 1 }} // remove default margin + expand input
																			>
																				<Input placeholder="Person name" disabled />
																			</Form.Item>
																			{screen !== 'view' && (
																				<Button
																					type="dashed"
																					shape="circle"
																					icon={<UsergroupAddOutlined />}
																					onClick={() => {
																						setShowModal(true);
																						setSelectedModalRow(field.name);
																						setSelectedModalRow({
																							name: 'committeeSecretary',
																							index: field.name,
																						});
																					}}
																				/>
																			)}
																		</Box>
																	</TableCell>

																	<TableCell>
																		<Form.Item
																			name={[field.name, 'nominatedAs']}
																			rules={[
																				{
																					required: true,
																					message: 'Committee role is required.',
																				},
																			]}
																		>
																			<Select
																				allowClear
																				showSearch
																				style={{ width: '100%' }}
																				placeholder="Select from the list"
																				options={[
																					{
																						label: 'Position',
																						value: 'positions',
																					},
																					{
																						label: 'Employee',
																						value: 'users',
																					},
																					{
																						label: 'Process Role',
																						value: 'processRoles',
																					},
																				]}
																				disabled={true}
																			/>
																		</Form.Item>
																	</TableCell>

																	{/* <TableCell align="center" sx={{ width: '10rem' }}>
																				<Form.Item
																					name={[field.name, 'positionName']}
																					rules={[
																						{
																							required: true,
																							message: 'Committee role is required.',
																						},
																					]}
																				>
																					<Select
																						allowClear
																						showSearch
																						style={{ width: '100%' }}
																						placeholder="Select from the list"
																						options={roleList}
																					/>
																				</Form.Item>
																			</TableCell>

																			<TableCell align="center" sx={{ width: '10rem' }}>
																				<Form.Item name={[field.name, 'canVote']}>
																					<Select
																						allowClear
																						showSearch
																						style={{ width: '100%' }}
																						placeholder="Select from the list"
																						options={[
																							{ label: 'Yes', value: 'yes' },
																							{ label: 'No', value: 'no' },
																						]}
																					/>
																				</Form.Item>
																			</TableCell>

																			<TableCell align="center" sx={{ width: '10rem' }}>
																				<Form.Item name={[field.name, 'isMandatory']}>
																					<Select
																						allowClear
																						showSearch
																						style={{ width: '100%' }}
																						placeholder="Select from the list"
																						options={[
																							{ label: 'Yes', value: 'yes' },
																							{ label: 'No', value: 'no' },
																						]}
																					/>
																				</Form.Item>
																			</TableCell>

																			<TableCell align="center" sx={{ width: '10rem' }}>
																				<Form.Item
																					name={[field.name, 'points']}
																					style={{ margin: 0, flex: 1 }} // remove default margin + expand input
																				>
																					<Input placeholder="" />
																				</Form.Item>
																			</TableCell> */}

																	{screen !== 'view' && (
																		<TableCell align="center">
																			{fields.length === 1 && index === 0 && (
																				<Tooltip title="Add Row">
																					<AddIconButton onClick={() => fieldsOpt.add()} />
																				</Tooltip>
																			)}

																			{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																				<Tooltip title="Delete Row">
																					<DeleteIconButton onClick={() => fieldsOpt.remove(field.name)} />
																				</Tooltip>
																			)}

																			{fields.length > 1 && index === fields.length - 1 && (
																				<>
																					<Tooltip title="Add Row">
																						<AddIconButton onClick={() => fieldsOpt.add()} />
																					</Tooltip>
																					<Tooltip title="Delete Row">
																						<DeleteIconButton
																							onClick={() => fieldsOpt.remove(field.name)}
																						/>
																					</Tooltip>
																				</>
																			)}

																			{fields.length > 1 && index === 0 && <span></span>}
																		</TableCell>
																	)}
																</TableRow>
																{/* </DraggableRow> */}
															</SortableContext>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										)}
									</Form.List>
									{/* </MainCard> */}
									<Col xs={24} sm={24} md={12} xl={12}>
										<Form.Item
											label="Holding Time Unit"
											name="holdingTime"
											rules={[
												{
													required: true,
													message: 'Holding Time Unit is required',
												},
												({ getFieldValue }) => ({
													validator(_, value) {
														const quorum = getFieldValue('quorum');
														if (quorum === 0 && value !== 0) {
															return Promise.reject(new Error('Holding Time must be 0 when Quorum is 0'));
														}
														return Promise.resolve();
													},
												}),
											]}
										>
											<InputNumber
												min={0}
												style={{ width: '100%' }}
												placeholder="Write here"
												disabled={screen === 'view' ? true : false}
											/>
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={12} xl={12}>
										<Form.Item
											label="Holding Time Unit Of Measurements"
											name="holdingTimeUnit"
											rules={[
												{
													required: true,
													message: 'Holding Time Unit Of Measurements is required',
												},
											]}
										>
											{/* <InputNumber min={1} style={{ width: '100%' }} placeholder="Write here" /> */}
											<Select
												allowClear
												showSearch
												style={{ width: '100%' }}
												placeholder="Select from the list"
												disabled={screen === 'view' ? true : false}
												options={[
													{
														label: 'Hour (s)',
														value: 'hour',
													},
													{
														label: 'Day (s)',
														value: 'days',
													},
													{
														label: 'Week (s)',
														value: 'week',
													},
													{
														label: 'Month (s)',
														value: 'month',
													},
												]}
											/>
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={12} xl={12}>
										<Form.Item
											label="Approval"
											name="approval"
											rules={[
												{
													required: true,
													message: 'Approval is required',
												},
											]}
										>
											<Radio.Group disabled={screen === 'view' ? true : false}>
												<Radio value="cWin">Chairside wins</Radio>
												<Radio value="cLoss">Chairside losses</Radio>
											</Radio.Group>
										</Form.Item>
									</Col>

									<Col xs={24}>
										<IdentityActionButton
											{...{
												detail:
													singleIdentityData?.parkedData &&
													Object.keys(singleIdentityData?.parkedData)?.length > 0 &&
													(state?.tab === 'W-I-P' || state?.tab === 'Worklist')
														? singleIdentityData?.parkedData
														: singleIdentityData?.data,
												history: singleIdentityData?.history,
												id: state?._id,
												dateTime: singleIdentityData?.dateTime,
												processCode: singleIdentityData?.processCode,
												processName: state?.processName,
												tab: state?.tab,
												processVersion: singleIdentityData?.parkProcessVersion,
												activationStatus: singleIdentityData?.activationStatus,
												recordStatus: singleIdentityData?.recordStatus,
												approverUserId: singleIdentityData?.approverUserId,
												parkAction: singleIdentityData?.parkAction,
												version: +singleIdentityData?.version,
												permissions,
												deactivateHandlerFunc: () => handleSubmit({ actionType: 'activeDeactivate' }),
												approveRejectHandlerFunc: (type) => handleSubmit({ actionType: type }),
												navigateToEditFunc: navigateToEdit,
												screen,
												isOperationLoading,
												isHistoryHidden: true,
												action,
											}}
										>
											<Button
												icon={<img src={historyIcon} width={16} height={16} alt="edit" />}
												onClick={(e) => {
													e.stopPropagation();

													callApiHistory();
												}}
												style={{
													height: '36px',
													paddingInline: '16px',
													borderRadius: '10px',
													fontSize: '14px',
													fontWeight: 500,
													display: 'inline-flex',
													alignItems: 'center',
												}}
											>
												{historyState ? 'Hide History' : 'View History'}
											</Button>
										</IdentityActionButton>
									</Col>
								</Row>
							</Form>

							{showModal && (
								<SearchMemberModal
									formName={selectedModalRow.name}
									initialValue={form.getFieldValue([selectedModalRow.name, selectedModalRow.index])}
									dataList={dataList}
									showModal={showModal}
									handleClose={() => {
										setShowModal(false);
										setSelectedModalRow(null);
									}}
								/>
							)}
						</Form.Provider>
					)}
				</SectionCard>

				{isGetHistorySuccess && historyState && historyData && (
					// <Grid item xs={24} sx={{ mb: 5 }}>
					<Box sx={{ mt: 2 }}>
						{historyData?.result?.length > 0 &&
							historyData?.result?.map((item, index) => {
								return (
									<Collapse
										key={index}
										style={{ marginBottom: '10px' }}
										items={[
											{
												key: index.toString(),
												label: <b>Current: -{(index + 1).toString()}</b>,
												children: (
													<HistoryCom
														historyData={item}
														isSuccess={isGetHistorySuccess}
														setHistoryState={setHistoryState}
														historyState={historyState}
														dataList={dataList}
														roleList={roleList}
													/>
												),
											},
										]}
										defaultActiveKey={['0']}
									></Collapse>
								);
							})}
						{historyData?.result?.length === 0 && (
							<Collapse
								style={{ marginBottom: '10px' }}
								items={[
									{
										key: '0',
										label: <b>History</b>,
										children: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />,
									},
								]}
								defaultActiveKey={['0']}
							></Collapse>
						)}
					</Box>
					// </Grid>
				)}
			</Box>

			{confirmModal && (
				<ComptrollerConfirmModal
					modalData={modalData}
					showModal={confirmModal}
					setShowModal={setConfirmModal}
					handleSubmit={() =>
						action === 'activeDeactivate'
							? handleActiveDeactivate(singleIdentityData?.activationStatus)
							: action === 'approve-single' || action === 'reject-single'
								? handleApproveReject(action)
								: action === 'create'
									? handleCreate(formSubmitData)
									: action === 'update'
										? handleUpdate(formSubmitData)
										: null
					}
				/>
			)}
		</>
	);
};

export default Edit;
